import React, { FC, useMemo } from 'react';
import { MessageDirection, TwilioMessage, MessageAuthorGroup } from 'models/twilio';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import { COLORS, IE } from 'components/twilio/constants';
import toDateDisplayText from 'components/twilio/utils/toDateDisplayText';
import MessageGroup from './MessageGroup';
import SenderWrapper from './SenderWrapper';
import SystemMessageHandler from './SystemMessageHandler';

const PrivateChatWrapper = styled.div`
  margin: 8px 0;
`;

const DateContainer = styled.div`
  margin: 8px auto;
  display: flex;
  justify-content: center;
  z-index: 1;
  ${!IE && `
    position: sticky;
    top: 0;
  `}
`;

const DateTextContainer = styled.div`
  padding: 4px 8px;
  font-size: 14px;
  background-color: rgba(211, 211, 211, 0.5);
  color: ${COLORS.TEXT_DARK};
  border-radius: 4px;
`;

const MessageDateGroupContainer = styled.div`
  ${IE && `
    display: ruby;
  `}
`;

export interface AuthorMessageGroupProps {
  direction: MessageDirection;
  messages: TwilioMessage[];
  author: string;
  renderSender: boolean;
}

const AuthorMessageGroup: FC<AuthorMessageGroupProps> = React.memo(({
  author,
  direction,
  messages,
  renderSender,
}: AuthorMessageGroupProps) => {
  const Messages = useMemo(() => (
    <MessageGroup
      direction={direction}
      messages={messages}
    />
  ), [messages, direction]);

  if (author === 'SYSTEM') {
    return <SystemMessageHandler messages={messages} />;
  }

  if (direction === MessageDirection.INCOMING && renderSender) {
    return (
      <SenderWrapper author={author}>
        {Messages}
      </SenderWrapper>
    );
  }

  return (
    <PrivateChatWrapper>
      {Messages}
    </PrivateChatWrapper>
  );
});

const DateText: FC<{ dateString: string }> = ({ dateString }) => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  return (
    <DateTextContainer>
      {toDateDisplayText(dateString, t, locale)}
    </DateTextContainer>
  );
};

export interface MessageDateGroupProps {
  dateString: string;
  messageGroups: MessageAuthorGroup[];
}

const MessageDateGroup: FC<MessageDateGroupProps> = ({ dateString, messageGroups }) => (
  <MessageDateGroupContainer>
    <DateContainer>
      <DateText dateString={dateString} />
    </DateContainer>
    {messageGroups.map(({ startMessageIndex, ...groupProps }) => (
      <AuthorMessageGroup
        key={`AuthorMessageGroup-${startMessageIndex}`}
        {...groupProps}
      />
    ))}
  </MessageDateGroupContainer>
);

export default MessageDateGroup;
